import type { KeyboardEvent } from 'react';

import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { Image } from '@/libraries/design/components/image/src/image';

import { WorkStyleChoiceCardBackground } from './work-style-choice-card-background';

import type { TemplateOption } from '../../components/template-choice/template-definitions';

type WorkStyleChoiceCardProps = {
	templateOption: TemplateOption;
	isSelected: boolean;
	onSelect: (templateOption: TemplateOption) => void;
	onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
};

const cardStyles = css({
	display: 'flex',
	flexDirection: 'column',
	flex: '1 1 0',
	minWidth: 0,
	maxWidth: '320px',
	margin: 0,
	padding: 0,
	overflow: 'hidden',
	textAlign: 'start',
	cursor: 'pointer',
	backgroundColor: token('elevation.surface'),
	position: 'relative',
	border: 'none',
	borderRadius: token('radius.large', '12px'),
	transition: 'box-shadow 0.2s ease-in-out',
	'&::after': {
		content: '""',
		position: 'absolute',
		inset: 0,
		borderRadius: 'inherit',
		boxShadow: `inset 0 0 0 ${token('border.width', '1px')} ${token('color.border')}`,
		pointerEvents: 'none',
	},
	'&:hover': {
		boxShadow: token('elevation.shadow.raised'),
	},
	'&:focus-visible': {
		outline: 'none',
	},
	'&:focus-visible::after': {
		boxShadow: `inset 0 0 0 ${token('border.width.selected', '2px')} ${token('color.border.focused')}`,
	},
});

const selectedCardStyles = css({
	'&::after': {
		boxShadow: `inset 0 0 0 ${token('border.width.selected', '2px')} ${token('color.border.selected')}`,
	},
});

const previewStyles = css({
	display: 'block',
	position: 'relative',
	width: '100%',
	height: '220px',
	overflow: 'hidden',
	backgroundColor: token('color.background.accent.blue.subtlest'),
});

const previewLinesStyles = css({
	display: 'block',
	position: 'absolute',
	insetBlockStart: 0,
	insetInlineStart: 0,
	width: '100%',
	height: '100%',
	color: token('color.background.accent.blue.subtler'),
});

const rotatedPreviewLinesStyles = css({
	transform: 'rotate(180deg)',
});

const previewImageStyles = css({
	display: 'block',
	position: 'relative',
	width: '100%',
	height: '100%',
	boxSizing: 'border-box',
	paddingBlockStart: '36px',
	paddingInlineEnd: '16%',
	paddingBlockEnd: '36px',
	paddingInlineStart: '16%',
	objectFit: 'contain',
});

const bodyStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
	padding: token('space.300'),
});

const titleStyles = css({
	font: token('font.heading.small'),
	color: token('color.text'),
});

const descriptionStyles = css({
	font: token('font.body'),
	color: token('color.text.subtle'),
});

export const WorkStyleChoiceCard = ({
	templateOption,
	isSelected,
	onSelect,
	onKeyDown,
}: WorkStyleChoiceCardProps) => {
	const { imageSrc, title, subtext, templateType } = templateOption.option;

	return (
		<button
			type="button"
			role="radio"
			aria-checked={isSelected}
			tabIndex={isSelected ? 0 : -1}
			css={[cardStyles, isSelected && selectedCardStyles]}
			onClick={() => onSelect(templateOption)}
			onKeyDown={onKeyDown}
		>
			<span css={previewStyles}>
				<span
					css={[previewLinesStyles, templateType === 'scrum' && rotatedPreviewLinesStyles]}
					data-testid="work-style-choice-card-lines"
				>
					<WorkStyleChoiceCardBackground />
				</span>
				<Image src={imageSrc} alt="" css={previewImageStyles} />
			</span>
			<span css={bodyStyles}>
				<span css={titleStyles}>{title}</span>
				<span css={descriptionStyles}>{subtext}</span>
			</span>
		</button>
	);
};
